import { useState } from 'react';
import { Badge } from '@/shared/components/ui/Badge';
import { BackdatedIndicator } from '@/shared/components/ui/BackdatedIndicator';
import { AgingBadge } from './AgingBadge';
import { isOrderBackdated, formatDate } from '@/shared/utils/dateUtils';
import type { FrameReplenishmentItem } from '@/shared/types/domain.types';

interface ReceivingTableProps {
  frames: FrameReplenishmentItem[];
  selectedFrameIds: string[];
  onSelectionChange: (frameIds: string[]) => void;
}

type SortField = 'ordered_date' | 'brand' | 'frame_name_or_model' | 'location' | 'po_number';
type SortDirection = 'asc' | 'desc';

export function ReceivingTable({
  frames,
  selectedFrameIds,
  onSelectionChange,
}: ReceivingTableProps) {
  const [sortField, setSortField] = useState<SortField>('ordered_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const allSelected = frames.length > 0 && frames.every((f) => selectedFrameIds.includes(f.id));
  const someSelected = frames.some((f) => selectedFrameIds.includes(f.id)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      const remaining = selectedFrameIds.filter((id) => !frames.find((f) => f.id === id));
      onSelectionChange(remaining);
    } else {
      // Select all
      const allIds = [...new Set([...selectedFrameIds, ...frames.map((f) => f.id)])];
      onSelectionChange(allIds);
    }
  };

  const handleSelectFrame = (frameId: string) => {
    if (selectedFrameIds.includes(frameId)) {
      onSelectionChange(selectedFrameIds.filter((id) => id !== frameId));
    } else {
      onSelectionChange([...selectedFrameIds, frameId]);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedFrames = [...frames].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (sortField) {
      case 'ordered_date':
        aVal = new Date(a.ordered_date || '').getTime();
        bVal = new Date(b.ordered_date || '').getTime();
        break;
      case 'brand':
        aVal = a.brand.toLowerCase();
        bVal = b.brand.toLowerCase();
        break;
      case 'frame_name_or_model':
        aVal = a.frame_name_or_model.toLowerCase();
        bVal = b.frame_name_or_model.toLowerCase();
        break;
      case 'location':
        aVal = a.location?.name.toLowerCase() || '';
        bVal = b.location?.name.toLowerCase() || '';
        break;
      case 'po_number':
        aVal = (a.po_number || '').toLowerCase();
        bVal = (b.po_number || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-gray-400">⇅</span>;
    return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (frames.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No ordered frames found. Frames must be ordered before they can be received.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-12 px-3 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected)}
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('location')}
            >
              <div className="flex items-center gap-1">
                Location <SortIcon field="location" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('brand')}
            >
              <div className="flex items-center gap-1">
                Brand <SortIcon field="brand" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('frame_name_or_model')}
            >
              <div className="flex items-center gap-1">
                <span className="hidden lg:inline">Frame Name/Model</span>
                <span className="lg:hidden">Frame</span>
                <SortIcon field="frame_name_or_model" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              Color
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              Size
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              <span className="hidden md:inline">Order Type</span>
              <span className="md:hidden">Type</span>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              <span className="hidden md:inline">Patient / Tray</span>
              <span className="md:hidden">P / T</span>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('po_number')}
            >
              <div className="flex items-center gap-1">
                <span className="hidden md:inline">PO Number</span>
                <span className="md:hidden">PO #</span>
                <SortIcon field="po_number" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('ordered_date')}
            >
              <div className="flex items-center gap-1">
                <span className="hidden md:inline">Ordered Date</span>
                <span className="md:hidden">Ordered</span>
                <SortIcon field="ordered_date" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              Aging
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedFrames.map((frame) => (
            <tr
              key={frame.id}
              className={`hover:bg-gray-50 ${selectedFrameIds.includes(frame.id) ? 'bg-blue-50' : ''}`}
            >
              <td className="px-3 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedFrameIds.includes(frame.id)}
                  onChange={() => handleSelectFrame(frame.id)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                <span className="hidden lg:inline">{frame.location?.name || 'Unknown'}</span>
                <span className="lg:hidden">{frame.location?.short_code || frame.location?.name || 'Unknown'}</span>
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.brand}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.frame_name_or_model}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.color}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.eye_size}
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                <Badge variant={frame.order_type === 'STOCK' ? 'blue' : 'purple'} size="sm">
                  {frame.order_type}
                </Badge>
              </td>
              <td className="px-3 py-4 text-sm text-gray-900">
                {frame.order_type === 'PATIENT' && frame.patient_name && (
                  <div>
                    <div className="font-medium">{frame.patient_name}</div>
                    {frame.tray_number && (
                      <div className="text-xs text-gray-500">Tray: {frame.tray_number}</div>
                    )}
                  </div>
                )}
                {frame.order_type === 'STOCK' && <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.po_number || <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <span>{formatDate(frame.ordered_date)}</span>
                  <BackdatedIndicator
                    isBackdated={isOrderBackdated(frame.ordered_date, frame.created_at)}
                    variant="text"
                  />
                </div>
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                {frame.ordered_date && <AgingBadge orderedDate={frame.ordered_date} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
